import {RunSignature} from '../../../types/run';
import {TileMediaSpec} from '../../MediaCard';
import {fetchDirectURLByArtifactURI} from './artifacts';
import {getLastAvailableFilePathForTileMediaSpec} from './availableFiles';
import {fetchDirectURLByFilePath} from './batching';
import IN_MEMORY_CACHE, {updateTracking} from './inMemoryCache';
import {
  getKeyForRunSignature,
  getKeyForTileMediaSpec,
  RunSignatureKey,
  TileMediaSpecKey,
} from './keys';
import {PathSpec} from './types';
import {updateWithNewData} from './update';
import {groupTileMediaSpecsByRunSignatureKey} from './util';

/**
 * The process for fetching new data is broken up into these primary steps:
 * 1. Take the tile media specs and group them by run signature key.
 * TileMediaSpec[] -> Map<string, TileMediaSpec[]>
 * 2. Get media file paths based on metadata (run, step, index)
 * 3. Get download URLs for media based on file paths
 */
export async function fetchNewTileMedia(
  tileMediaSpecs: TileMediaSpec[],
  attemptNonexistentSteps: boolean
): Promise<void> {
  // STEP ONE: Group tile media specs by run signature key.
  // multiple media specs can share a single run signature key
  const tileMediaSpecsByRunSignatureKey =
    groupTileMediaSpecsByRunSignatureKey(tileMediaSpecs);

  /**
   * STEP 2: Get all the media file paths organized by the TileMediaSpecKey. Filter off all the media specs where the file upload may have started but not yet completed.
   */
  const existingFilePathByTileMediaSpecKey =
    await fetchExistingFilePathByKeyMap(
      tileMediaSpecsByRunSignatureKey,
      attemptNonexistentSteps
    );

  // STEP 3: For each valid file path keyed by a TileMediaSpecKey, get the direct URL.
  const [
    directURLByRunFilePathOrArtifactURI,
    internalURLByRunFilePathOrArtifactURI,
  ] = await fetchDirectURLByRunFilePathOrArtifactURIMap(
    tileMediaSpecsByRunSignatureKey,
    existingFilePathByTileMediaSpecKey
  );

  // Download new media and update global cache used by the Media Panel to display media
  await updateWithNewData(
    tileMediaSpecsByRunSignatureKey,
    existingFilePathByTileMediaSpecKey,
    directURLByRunFilePathOrArtifactURI,
    internalURLByRunFilePathOrArtifactURI || new Map()
  );
}

type FilePathsByTileMediaSpecKey = Map<TileMediaSpecKey, PathSpec>;

/**
 * We fetch a mapping of tile -> existing file path. We can't just use the file path in the history without verifying its existence because there is a delay in uploading files, so the files specified by paths in history may not exist yet.
 */

async function fetchExistingFilePathByKeyMap(
  tileMediaSpecsByRunSignatureKey: Map<RunSignatureKey, TileMediaSpec[]>,
  attemptNonexistentSteps: boolean
) {
  const result: FilePathsByTileMediaSpecKey = new Map();

  const getExistingFilePathsPromises: Array<Promise<void>> = [];
  for (const [, tileMediaSpecs] of tileMediaSpecsByRunSignatureKey) {
    for (const tileMediaSpec of tileMediaSpecs) {
      getExistingFilePathsPromises.push(
        getExistingFilePath(tileMediaSpec, attemptNonexistentSteps, result)
      );
    }
  }
  await Promise.all(getExistingFilePathsPromises);

  return result;
}

async function getExistingFilePath(
  tileMediaSpec: TileMediaSpec,
  attemptNonexistentSteps: boolean,
  existingFilePathByKeyMap: FilePathsByTileMediaSpecKey
): Promise<void> {
  const stepNumber = tileMediaSpec.mediaStep;
  const existingFilePath = await getLastAvailableFilePathForTileMediaSpec({
    tileMediaSpec,
    attemptNonexistentSteps,
    stepNumber,
  });
  if (existingFilePath == null) {
    return;
  }

  const tileMediaSpecKey = getKeyForTileMediaSpec(tileMediaSpec);
  existingFilePathByKeyMap.set(tileMediaSpecKey, existingFilePath);
}

function getFilePathsAndArtifactURIsForRun(
  tileMediaSpecs: TileMediaSpec[],
  existingFilePathByKey: Map<TileMediaSpecKey, PathSpec>
): {
  filePaths: string[];
  artifactURIs: string[];
} {
  const filePaths: string[] = [];
  const artifactURIs: string[] = [];
  for (const tileMediaSpec of tileMediaSpecs) {
    const tileMediaSpecKey = getKeyForTileMediaSpec(tileMediaSpec);

    const pathSpec = existingFilePathByKey.get(tileMediaSpecKey);
    if (pathSpec == null) {
      continue;
    }

    if (`filePath` in pathSpec) {
      filePaths.push(pathSpec.filePath);
    } else if (`artifactURI` in pathSpec) {
      artifactURIs.push(pathSpec.artifactURI);
    }
  }
  return {filePaths, artifactURIs};
}

// We fetch a mapping of file path / artifact reference -> direct download URLs.
async function fetchDirectURLByRunFilePathOrArtifactURIMap(
  tileMediaSpecsByRunSignatureKey: Map<RunSignatureKey, TileMediaSpec[]>,
  existingFilePathByKey: Map<TileMediaSpecKey, PathSpec>
) {
  const directUrls: Map<`${RunSignatureKey}-${string}`, string> = new Map();
  const internalUrls: Map<`${RunSignatureKey}-${string}`, string> = new Map();

  const getDirectURLPromises: Array<Promise<void | void[]>> = [];
  for (const [, tileMediaSpecs] of tileMediaSpecsByRunSignatureKey) {
    // We batch this process by run since it's possible to request download URLs for multiple files at once per run.
    const {runSignature} = tileMediaSpecs[0];
    const {filePaths, artifactURIs} = getFilePathsAndArtifactURIsForRun(
      tileMediaSpecs,
      existingFilePathByKey
    );
    getDirectURLPromises.push(
      fetchFilePaths(runSignature, filePaths, tileMediaSpecs)
    );
    getDirectURLPromises.push(fetchArtifactURIs(runSignature, artifactURIs));
  }
  await Promise.all(getDirectURLPromises);

  return [directUrls, internalUrls] as const;

  async function fetchFilePaths(
    runSignature: RunSignature,
    filePaths: string[],
    tileMediaSpecs: TileMediaSpec[]
  ): Promise<void> {
    if (filePaths.length === 0) {
      return;
    }
    const [directURLByFilePathForRun, internalURLByFilePathForRun] =
      await fetchDirectURLByFilePath({
        runSignature,
        filePaths,
      }).then(result => {
        if (result != null) {
          for (const tileMediaSpec of tileMediaSpecs) {
            updateTracking(
              getKeyForTileMediaSpec(tileMediaSpec),
              'directUrlReceivedMs'
            );
          }
        }
        return result;
      });
    if (directURLByFilePathForRun == null) {
      return;
    }
    directURLByFilePathForRun.forEach((directURL, filePath) =>
      directUrls.set(
        `${getKeyForRunSignature(runSignature)}-${filePath}`,
        directURL
      )
    );

    if (internalURLByFilePathForRun == null) {
      return;
    }
    internalURLByFilePathForRun.forEach((internalURL, filePath) =>
      internalUrls.set(
        `${getKeyForRunSignature(runSignature)}-${filePath}`,
        internalURL
      )
    );
  }

  async function fetchArtifactURIs(
    runSignature: RunSignature,
    artifactURIs: string[]
  ): Promise<void[]> {
    if (artifactURIs.length === 0) {
      return [];
    }
    return Promise.all(
      artifactURIs.map(uri => fetchArtifactURI(runSignature, uri))
    );
  }

  async function fetchArtifactURI(
    runSignature: RunSignature,
    uri: string
  ): Promise<void> {
    const directURL = await fetchDirectURLByArtifactURI(uri);
    if (directURL == null) {
      return;
    }
    directUrls.set(`${getKeyForRunSignature(runSignature)}-${uri}`, directURL);
  }
}

// Used so the Media Panel can enable steps only when media is available for those steps
export async function fetchMaxStepWithAvailableMedia(
  tileMediaSpecs: TileMediaSpec[],
  attemptNonexistentSteps: boolean
): Promise<number | null> {
  await fetchNewTileMedia(tileMediaSpecs, attemptNonexistentSteps);

  let maxStepWithAvailableMedia: number | null = null;
  for (const tileMediaSpec of tileMediaSpecs) {
    const key = getKeyForTileMediaSpec(tileMediaSpec);
    const exists = IN_MEMORY_CACHE.TILE_MEDIA_BY_KEY.has(key);
    if (
      exists &&
      (maxStepWithAvailableMedia == null ||
        tileMediaSpec.mediaStep > maxStepWithAvailableMedia)
    ) {
      maxStepWithAvailableMedia = tileMediaSpec.mediaStep;
    }
  }
  return maxStepWithAvailableMedia;
}
